import { useMemo } from "react";
import { Link } from "react-router-dom";
import { fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/shared";

const HEALTH = { on_track: "bg-emerald-500", at_risk: "bg-amber-500", overdue: "bg-rose-500", done: "bg-slate-400" };
const HEALTH_LABEL = { on_track: "On track", at_risk: "At risk", overdue: "Overdue", done: "Done" };
const day = (s) => new Date(s.length === 10 ? s + "T00:00:00" : s);

export function Timeline({ orders, today, testId = "timeline" }) {
  const rows = useMemo(() => orders.filter((o) => o.start_date || o.due_date || o.created_at), [orders]);
  const { start, months, total } = useMemo(() => {
    const dates = rows.flatMap((o) => [o.start_date || o.created_at?.slice(0, 10), o.due_date, ...(o.milestones || []).map((m) => m.due_date)]).filter(Boolean).map(day);
    dates.push(day(today));
    const min = new Date(Math.min(...dates)), max = new Date(Math.max(...dates));
    const s = new Date(min.getFullYear(), min.getMonth(), 1);
    const e = new Date(max.getFullYear(), max.getMonth() + 1, 1);
    const ms = []; for (let d = new Date(s); d < e; d.setMonth(d.getMonth() + 1)) ms.push(new Date(d));
    return { start: s, months: ms, total: (e - s) / 86400000 };
  }, [rows, today]);
  const pct = (d) => `${Math.max(0, Math.min(100, ((day(d) - start) / 86400000 / total) * 100))}%`;
  if (!rows.length) return <div className="panel border-dashed p-10 text-center text-sm text-slate-500" data-testid={`${testId}-empty`}>No dated work orders yet.</div>;
  return (
    <div className="panel p-5 overflow-x-auto" data-testid={testId}>
      <div className="min-w-[720px]">
        <div className="grid" style={{ gridTemplateColumns: "220px 1fr" }}>
          <div />
          <div className="relative h-7 border-b border-slate-200 flex">
            {months.map((m) => <div key={m.toISOString()} className="text-[11px] uppercase tracking-wider text-slate-500 border-l border-slate-100 pl-2" style={{ width: `${(new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate() / total) * 100}%` }}>{m.toLocaleString("en", { month: "short", year: "2-digit" })}</div>)}
          </div>
        </div>
        <div className="relative">
          <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-blue-500 z-10" style={{ left: `calc(220px + (100% - 220px) * ${parseFloat(pct(today)) / 100})` }} data-testid="timeline-today"><span className="absolute -top-0.5 left-1 text-[10px] font-bold text-blue-600">Today</span></div>
          {rows.map((o) => {
            const s = o.start_date || o.created_at.slice(0, 10), e = o.due_date || s;
            return (
              <div key={o.id} className="grid items-center py-2.5 border-b border-slate-50" style={{ gridTemplateColumns: "220px 1fr" }} data-testid={`timeline-row-${o.id}`}>
                <div className="pr-3 min-w-0"><Link to={`/projects/${o.id}`} className="text-sm font-medium text-slate-900 hover:text-blue-600 truncate block">{o.name}</Link><div className="text-[11px] text-slate-500 flex items-center gap-1.5"><span className="num">{o.wo_number}</span><StatusBadge status={o.client_status} /></div></div>
                <div className="relative h-7">
                  <div className={`absolute top-1.5 h-4 rounded-full ${HEALTH[o.health]} opacity-90`} style={{ left: pct(s), width: `max(6px, calc(${pct(e)} - ${pct(s)}))` }} title={`${fmtDate(s)} → ${fmtDate(e)} · ${HEALTH_LABEL[o.health]}`} />
                  {(o.milestones || []).filter((m) => m.due_date).map((m) => <div key={m.id} className={`absolute top-1 h-5 w-5 -ml-2.5 rounded-full border-2 border-white shadow ${m.done ? "bg-emerald-600" : "bg-white ring-1 ring-slate-300"}`} style={{ left: pct(m.due_date) }} title={`${m.title} · ${fmtDate(m.due_date)}`} />)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 text-[11px] text-slate-500">{Object.entries(HEALTH_LABEL).map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${HEALTH[k]}`} />{v}</span>)}</div>
      </div>
    </div>
  );
}
